<template>
  <!-- Editor mode: wrap so drop-target detection (`closest('[data-slot-target]')`)
       has the slot attributes to find. Non-editor modes skip the wrapper
       entirely and render children inline. -->
  <div v-if="isEditor" class="render-slot" :data-slot-target="parentNodeId" :data-slot-name="name">
    <RenderComponents
      v-if="hasContent"
      :schema="{ children: props.children! }"
      :ComponentRegistry="registry"
      :editable="editable"
      :shared-node-internal="sharedNodeInternal"
      @update:schema-fields="
        (id: string, val: ComponentNode['fields']) => emit('update:schema-fields', id, val)
      "
    />
    <slot v-else />
  </div>
  <RenderComponents
    v-else-if="hasContent"
    :schema="{ children: props.children! }"
    :ComponentRegistry="registry"
    :editable="editable"
    :shared-node-internal="sharedNodeInternal"
    @update:schema-fields="
      (id: string, val: ComponentNode['fields']) => emit('update:schema-fields', id, val)
    "
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, inject, getCurrentInstance } from 'vue'
import type { ComputedRef } from 'vue'
import type { ComponentNode } from '../api/types.gen'
import RenderComponents from './RenderComponents.vue'
import { PipeDisplayMode, usePipe } from './composables/usePipe'
import { provideStickyBounds } from './composables/useStickyBounds'

const props = defineProps<{
  name: string
  children?: ComponentNode[]
  editable?: boolean
  /** Nearest ancestor a nested StickyContainer's `fix-to-bounds` mode should clamp to. */
  boundsEl?: HTMLElement | null
}>()

provideStickyBounds(computed(() => props.boundsEl ?? null))

const sharedNodeInternal = inject<ComputedRef<boolean>>(
  'sharedNodeInternal',
  computed(() => false),
)

const pipe = usePipe()
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

const emit = defineEmits<{
  (e: 'update:schema-fields', nodeId: string, value: ComponentNode['fields']): void
}>()

const registry = inject<Record<string, { component: unknown }>>('renderComponentRegistry', {})
const hasContent = computed(() => (props.children?.length ?? 0) > 0)

// Discover the parent component's node ID from the DOM (editor-only, not needed for SSG)
const parentNodeId = ref<string | null>(null)
onMounted(() => {
  const el = getCurrentInstance()?.proxy?.$el as HTMLElement | undefined
  parentNodeId.value = el?.parentElement?.closest('[data-id]')?.getAttribute('data-id') ?? null
})
</script>

<style scoped>
/* `display: contents` makes this wrapper invisible to layout, so the slot's
   child (typically a single Container) participates directly in the outer
   layout context (e.g. the ColumnsOrRows grid) and inherits its alignment
   and size. DOM attributes (data-slot-target / data-slot-name) remain queryable
   by editor drop-zone detection. */
.render-slot {
  display: contents;
}
</style>
